import { PALETTE_MIME } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { Label } from "@aurumiq/ui/components/label";
import { Textarea } from "@aurumiq/ui/components/textarea";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { dmyTime, relativeTime } from "@aurumiq/ui/lib/format";
import { cn } from "@aurumiq/ui/lib/utils";
import { AlertTriangle, Eye, EyeOff, GripVertical, RotateCcw, Settings2 } from "lucide-react";
import { useId, useState } from "react";
import { Link } from "react-router";
import { SortableList } from "@/components/sortable-list";
import { BLOCK_LABELS, blockSummary, isBlockType } from "../editor/blocks";
import { SeoPanel } from "../editor/seo-panel";
import { useSiteSettings } from "../queries";
import { PALETTE, PALETTE_BY_TYPE } from "./defaults";
import type { DesignerBlock, DesignerState } from "./use-designer-state";

export function PanelTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="border-b px-4 py-3 pr-10">
      <div className="font-bold text-sm">{title}</div>
      {subtitle ? <div className="text-muted-foreground text-xs">{subtitle}</div> : null}
    </div>
  );
}

export function blockTitle(b: DesignerBlock): string {
  if (typeof b.label === "string" && b.label) return b.label;
  return isBlockType(b.type) ? BLOCK_LABELS[b.type] : `Unknown “${b.type}”`;
}

export function BlockIcon({ type, className }: { type: string; className?: string }) {
  const Icon = PALETTE_BY_TYPE[type]?.icon ?? Settings2;
  return <Icon className={cn("size-4", className)} />;
}

/* ---------- Add ---------- */

export function AddPanel({
  st,
  onInserted,
}: {
  st: DesignerState;
  onInserted: (label: string) => void;
}) {
  const target = st.selected;
  const after = target ? (st.page?.blocks.findIndex((b) => b.id === target.id) ?? -1) : -1;
  return (
    <div className="flex h-full flex-col">
      <PanelTitle
        title="Add block"
        subtitle={
          target
            ? `Inserts after “${blockTitle(target)}” — or drag onto the page`
            : "Click to append, or drag onto the page"
        }
      />
      <div className="flex-1 overflow-y-auto p-3">
        {PALETTE.map((p) => (
          <button
            key={p.type}
            type="button"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(PALETTE_MIME, p.type);
              e.dataTransfer.setData("text/plain", p.type);
              e.dataTransfer.effectAllowed = "copy";
            }}
            onClick={() => {
              st.insertBlock(p.type, after >= 0 ? after + 1 : undefined);
              onInserted(p.label);
            }}
            className="mb-2 flex w-full cursor-grab items-start gap-3 rounded-lg border bg-card p-3 text-left transition hover:border-gold hover:shadow-sm active:cursor-grabbing"
          >
            <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-navy">
              <p.icon className="size-4" />
            </span>
            <span className="min-w-0">
              <span className="block font-semibold text-sm">{p.label}</span>
              <span className="block text-muted-foreground text-xs leading-snug">
                {p.description}
              </span>
            </span>
            <GripVertical className="ml-auto mt-1 size-4 shrink-0 text-muted-foreground/50" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Layers ---------- */

export function LayersPanel({
  st,
  onScrollTo,
}: {
  st: DesignerState;
  onScrollTo: (id: string) => void;
}) {
  const blocks = st.page?.blocks ?? [];
  return (
    <div className="flex h-full flex-col">
      <PanelTitle
        title="Layers"
        subtitle={`${blocks.length} block${blocks.length === 1 ? "" : "s"} — drag to reorder`}
      />
      <div className="flex-1 overflow-y-auto p-2">
        {blocks.length === 0 ? (
          <p className="p-4 text-center text-muted-foreground text-sm">
            No blocks yet. Add one from the Add tab.
          </p>
        ) : null}
        <SortableList
          items={blocks}
          getId={(b) => b.id}
          ariaLabel="Layers"
          onReorder={(next) => st.reorderBlocks(next.map((b) => b.id))}
          rowClassName={(b) =>
            cn(
              b.id === st.selectedId && "border-gold ring-2 ring-gold/30",
              b.id === st.hoveredId && b.id !== st.selectedId && "border-navy/40",
              b.hidden === true && "opacity-50",
            )
          }
          renderItem={(b, i) => {
            const v = st.validations[i];
            return (
              <div className="flex min-w-0 flex-1 items-center gap-1 py-1 pr-1">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 py-1 text-left"
                  aria-current={b.id === st.selectedId ? "true" : undefined}
                  onMouseEnter={() => st.setHoveredId(b.id)}
                  onMouseLeave={() => st.setHoveredId(null)}
                  onClick={() => {
                    st.setSelectedId(b.id);
                    onScrollTo(b.id);
                  }}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded bg-muted text-navy">
                    {v && !v.ok ? (
                      <AlertTriangle className="size-3.5 text-danger" aria-label="Invalid block" />
                    ) : (
                      <BlockIcon type={b.type} className="size-3.5" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-sm">{blockTitle(b)}</span>
                    <span className="block truncate font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
                      {b.type}
                      {blockSummary(b) ? ` · ${blockSummary(b)}` : ""}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label={b.hidden === true ? `Show ${blockTitle(b)}` : `Hide ${blockTitle(b)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    st.toggleHidden(b.id);
                  }}
                >
                  {b.hidden === true ? (
                    <EyeOff className="size-3.5" />
                  ) : (
                    <Eye className="size-3.5" />
                  )}
                </button>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

export function PagePanel({ st }: { st: DesignerState }) {
  const id = useId();
  const p = st.page;
  if (!p) return null;
  return (
    <div className="flex h-full flex-col">
      <PanelTitle title="Page settings" subtitle="Saved together with the layout" />
      <div className="grid flex-1 content-start gap-4 overflow-y-auto p-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`${id}-kind`}>Kind</Label>
            <Input id={`${id}-kind`} value={p.kind} readOnly className="bg-muted" />
          </div>
          <div>
            <Label htmlFor={`${id}-status`}>Status</Label>
            <div className="flex h-9 items-center">
              <StatusTag variant={st.detail?.published ? "ok" : "warn"}>
                {st.detail?.published ? "PUBLISHED" : "DRAFT"}
              </StatusTag>
            </div>
          </div>
        </div>
        <div>
          <Label htmlFor={`${id}-excerpt`}>Excerpt</Label>
          <Textarea
            id={`${id}-excerpt`}
            rows={3}
            maxLength={400}
            value={p.excerpt}
            onChange={(e) => st.setExcerpt(e.target.value)}
          />
          <p className="mt-1 text-muted-foreground text-xs">Shown in post and changelog lists.</p>
        </div>
        <SeoPanel
          title={p.title}
          slug={p.slug}
          meta={p.meta}
          onTitle={st.setTitle}
          onSlug={st.setSlug}
          onMeta={st.setMeta}
          metaIssues={st.metaParsed.success ? undefined : st.metaParsed.error.issues}
        />
      </div>
    </div>
  );
}

/* ---------- Design ---------- */

export function DesignPanel() {
  const site = useSiteSettings();
  const s = site.data;
  return (
    <div className="flex h-full flex-col">
      <PanelTitle title="Design" subtitle="Site-wide look and defaults" />
      <div className="grid flex-1 content-start gap-3 overflow-y-auto p-4 text-sm">
        <p className="text-muted-foreground text-xs">
          Colours, fonts, navigation and SEO defaults are shared by every page and edited in Site
          settings. Per-block looks live in the inspector’s Style tab.
        </p>
        {s ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border p-3 text-xs">
            <dt className="text-muted-foreground">Site name</dt>
            <dd className="truncate">{s.site_name ?? "—"}</dd>
            <dt className="text-muted-foreground">Tagline</dt>
            <dd className="truncate">{s.tagline ?? "—"}</dd>
            <dt className="text-muted-foreground">Title template</dt>
            <dd className="truncate font-mono">{s.seo?.title_template ?? "—"}</dd>
            <dt className="text-muted-foreground">Nav items</dt>
            <dd>{s.nav?.length ?? 0}</dd>
          </dl>
        ) : null}
        <Button asChild variant="outline">
          <Link to="/cms/site" target="_blank" rel="noreferrer">
            <Settings2 /> Open site settings
          </Link>
        </Button>
      </div>
    </div>
  );
}

/* ---------- History ---------- */

export function HistoryPanel({
  st,
  onRevert,
}: {
  st: DesignerState;
  onRevert: (revisionId: string) => void;
}) {
  const d = st.detail;
  const [expanded, setExpanded] = useState(false);
  const revisions = d?.revisions ?? [];
  const shown = expanded ? revisions : revisions.slice(0, 20);
  return (
    <div className="flex h-full flex-col">
      <PanelTitle
        title="History"
        subtitle="Every save is a revision. Reverting replaces the draft; the live page is unchanged until you publish."
      />
      <div className="flex-1 overflow-y-auto p-2">
        {revisions.length === 0 ? (
          <p className="p-4 text-center text-muted-foreground text-sm">No revisions yet.</p>
        ) : null}
        <ul className="grid gap-1">
          {shown.map((r) => {
            const current = r.revision_id === d?.draft.revision_id;
            const live = r.revision_id === d?.published?.revision_id;
            return (
              <li
                key={r.revision_id}
                className={cn(
                  "flex items-center gap-2 rounded-lg border px-2 py-2 text-sm",
                  current && "border-gold bg-gold-bg/40",
                )}
              >
                <span className="font-mono text-xs">v{r.version}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{r.note ?? "—"}</span>
                  <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={dmyTime(r.created_at)}
                  >
                    {relativeTime(r.created_at)}
                    {r.created_by ? ` · ${r.created_by}` : ""}
                  </span>
                </span>
                {live ? <StatusTag variant="ok">LIVE</StatusTag> : null}
                {current ? (
                  <StatusTag>CURRENT</StatusTag>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Revert to v${r.version}`}
                    onClick={() => onRevert(r.revision_id)}
                    disabled={st.reverting}
                  >
                    <RotateCcw /> Revert
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        {revisions.length > 20 && !expanded ? (
          <Button variant="link" size="sm" onClick={() => setExpanded(true)}>
            Show all {revisions.length}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
